import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getDemoDataForCollection } from '../demoData';
import { 
  Handshake, Trash2, CheckCircle, Filter, Plus, ChevronDown, ChevronUp, 
  Eye, Globe, ShieldCheck, Sparkles, Upload, FileText, Check, X,
  Search, Building, User, Phone, Mail, MapPin, DollarSign, Award, Pin
} from 'lucide-react';
import { useAuth } from '../components/AuthGuard';

const PartnerDetailDrawer = ({ partner, onClose, onToggleMarquee }) => {
  if (!partner) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full md:w-[680px] lg:w-[780px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300 text-white">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 bg-zinc-900/60 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <img 
              src={partner.logoUrl || partner.media?.logoUrl || 'assets/dis_logo.svg'} 
              alt="logo" 
              className="w-14 h-14 rounded-2xl object-contain bg-white p-1.5 border border-zinc-700 shadow-md" 
            />
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="px-3 py-1 bg-[#8B1A2D]/20 text-[#8B1A2D] border border-[#8B1A2D]/40 text-[10px] font-black uppercase tracking-widest rounded-full">
                  Institutional Accord
                </span>
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${partner.isMarqueeLive !== false ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-zinc-800 text-zinc-500'}`}>
                  {partner.isMarqueeLive !== false ? 'Live Marquee' : 'Hidden'}
                </span>
              </div>
              <h2 className="text-2xl font-black text-white">{partner.name}</h2>
              <p className="text-xs text-zinc-400 font-bold">{partner.tagline || partner.partnershipType}</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
          {/* Quick Metrics */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Status</span>
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                {partner.status || 'Published'}
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Annual Funding</span>
              <span className="text-xs font-bold text-amber-400 font-mono">
                KES {(partner.fundingCommitment || 0).toLocaleString()}
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Active Placements</span>
              <span className="text-xs font-bold text-white font-mono">
                {partner.activePlacements || 0} Slots
              </span>
            </div>
          </div>

          {/* Section 1: Entity & Charter */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-[#8B1A2D] border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Building size={14} /> Legal Identity &amp; Accord Mandate
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Partner Legal Name:</span>
                <span className="text-zinc-200 font-bold">{partner.name}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Partnership Category:</span>
                <span className="text-zinc-200">{partner.partnershipType}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Registration / Charter No:</span>
                <span className="text-zinc-200 font-mono">{partner.registrationNumber || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">KRA PIN / Tax ID:</span>
                <span className="text-zinc-200 font-mono">{partner.kraPinNumber || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Location (County / Country):</span>
                <span className="text-zinc-200">{partner.county || 'Nairobi'}, {partner.country || 'Kenya'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Official Platform URL:</span>
                <span className="text-amber-300 font-mono">{partner.platformUrl || partner.integration?.platformUrl || '-'}</span>
              </div>
            </div>
          </div>

          {/* Section 2: Narrative & Notes */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-[#8B1A2D] border-b border-zinc-800 pb-2 flex items-center gap-2">
              <FileText size={14} /> Description &amp; Meeting Milestones
            </h3>
            <div className="space-y-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block mb-1">Legal Accord Narrative:</span>
                <p className="text-zinc-300 leading-relaxed bg-zinc-900 p-3 rounded-xl border border-zinc-800">
                  {partner.bodyText || partner.resources?.resourcesProvided || 'No extended accord description provided.'}
                </p>
              </div>
              {partner.meetingNotes && (
                <div>
                  <span className="text-zinc-500 font-bold block mb-1">Meeting Notes &amp; Joint Milestones:</span>
                  <p className="text-zinc-300 leading-relaxed bg-zinc-900 p-3 rounded-xl border border-zinc-800 font-mono text-[11px]">
                    {partner.meetingNotes}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Key POC Contacts */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-[#8B1A2D] border-b border-zinc-800 pb-2 flex items-center gap-2">
              <User size={14} /> Executive POC &amp; Liaison Leadership
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Primary Liaison Name:</span>
                <span className="text-zinc-200 font-bold">{partner.techContactName || partner.support?.name || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Liaison Email:</span>
                <span className="text-zinc-200 font-mono">{partner.techContactEmail || partner.email || partner.support?.email || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Contact Phone:</span>
                <span className="text-zinc-200 font-mono">{partner.phone || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Next Review Date:</span>
                <span className="text-zinc-200">{partner.nextReviewDate || 'Annual Review'}</span>
              </div>
            </div>
          </div>

          {/* Admin Marquee & Status Controls */}
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-zinc-400">Marquee Display</span>
            <button 
              onClick={() => onToggleMarquee(partner.id, partner.isMarqueeLive !== false)}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all cursor-pointer ${
                partner.isMarqueeLive !== false
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md'
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              {partner.isMarqueeLive !== false ? 'Live on Infinite Marquee' : 'Enable on Infinite Marquee'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default function PartnerManager({ showDemo, setShowDemo }) {
    const { role } = useAuth();
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [pinnedColumn, setPinnedColumn] = useState('name');

    // Comprehensive Registration & CMS Form State (Matching 100% of onboarding-partners.html)
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [name, setName] = useState('');
    const [partnershipType, setPartnershipType] = useState('Academic Accreditation Partner');
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [kraPinNumber, setKraPinNumber] = useState('');
    const [officialWebsite, setOfficialWebsite] = useState('');
    const [headquartersAddress, setHeadquartersAddress] = useState('');
    const [county, setCounty] = useState('Nairobi');
    const [country, setCountry] = useState('Kenya');

    const [tagline, setTagline] = useState('');
    const [bodyText, setBodyText] = useState('');
    const [meetingNotes, setMeetingNotes] = useState('');
    const [fundingCommitment, setFundingCommitment] = useState(0);
    const [activePlacements, setActivePlacements] = useState(0);
    const [startDate, setStartDate] = useState('');
    const [nextReviewDate, setNextReviewDate] = useState('');

    const [techContactName, setTechContactName] = useState('');
    const [techContactEmail, setTechContactEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [mouDocketUrl, setMouDocketUrl] = useState('');
    const [isMarqueeLive, setIsMarqueeLive] = useState(true);

    const fetchPartners = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'partners'));
            const data = [];
            snap.forEach(d => data.push({ id: d.id, ...d.data() }));

            if (showDemo) {
                data.unshift(
                  {
                    id: 'suny-potsdam',
                    name: 'SUNY Potsdam, USA',
                    partnershipType: 'Academic Accreditation Partner',
                    registrationNumber: 'NYSED/US/1816/001',
                    kraPinNumber: 'EXEMPT-INTL',
                    platformUrl: 'https://www.potsdam.edu',
                    headquartersAddress: '44 Pierrepont Avenue, Potsdam, NY',
                    county: 'New York',
                    country: 'United States',
                    tagline: 'State University of New York — Exclusive East African Academic Partner',
                    bodyText: 'Dynamic InduTechno Solutions (DIS) is the exclusive East African partner of SUNY Potsdam, USA (Est. 1816). All 120+ DIS programs are globally certified and carry full academic credit transferability.',
                    fundingCommitment: 2500000,
                    activePlacements: 50,
                    techContactName: 'Office of International Education',
                    techContactEmail: 'international@potsdam.edu',
                    phone: '+1 315 267 2000',
                    logoUrl: 'assets/partner_suny.png',
                    isMarqueeLive: true,
                    status: 'Published',
                    addedAt: '2026-06-01T08:00:00Z'
                  },
                  {
                    id: 'teau-partner',
                    name: 'The East African University',
                    partnershipType: 'University Campus Partner',
                    registrationNumber: 'CUE/UNIV/2012/08',
                    kraPinNumber: 'P051188339K',
                    platformUrl: 'https://teau.ac.ke',
                    headquartersAddress: 'Kitengela Campus Complex, Namanga Rd',
                    county: 'Kajiado',
                    country: 'Kenya',
                    tagline: 'Joint Degree & Technical Skills Execution Partner',
                    bodyText: 'The East African University (TEAU) partners with DIS to deliver blended practical campus workshops, joint certification programs, and state-of-the-art laboratory access.',
                    fundingCommitment: 1000000,
                    activePlacements: 300,
                    techContactName: 'Vice Chancellor Office',
                    techContactEmail: 'info@teau.ac.ke',
                    phone: '+254 722 000 333',
                    logoUrl: 'assets/partner_teau.jpg',
                    isMarqueeLive: true,
                    status: 'Published',
                    addedAt: '2026-06-15T10:00:00Z'
                  }
                );
            }
            setPartners(data);
        } catch (e) {
            console.error('Error fetching partners:', e);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchPartners();
    }, [showDemo]);

    const handleSavePartner = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setStatusMessage('saving');

        const partnerId = name.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');

        try {
            await setDoc(doc(db, 'partners', partnerId), {
                id: partnerId,
                name: name.trim(),
                partnershipType,
                registrationNumber: registrationNumber.trim(),
                kraPinNumber: kraPinNumber.trim(),
                platformUrl: officialWebsite.trim(),
                officialWebsite: officialWebsite.trim(),
                headquartersAddress: headquartersAddress.trim(),
                county: county.trim(),
                country: country.trim(),
                tagline: tagline.trim(),
                bodyText: bodyText.trim(),
                meetingNotes: meetingNotes.trim(),
                fundingCommitment: Number(fundingCommitment) || 0,
                activePlacements: Number(activePlacements) || 0,
                startDate,
                nextReviewDate,
                techContactName: techContactName.trim(),
                techContactEmail: techContactEmail.trim().toLowerCase(),
                email: techContactEmail.trim().toLowerCase(),
                phone: phone.trim(),
                logoUrl: logoUrl.trim(),
                mouDocketUrl: mouDocketUrl.trim(),
                isMarqueeLive,
                status: 'Published',
                updatedAt: new Date().toISOString()
            }, { merge: true });

            setName('');
            setRegistrationNumber('');
            setOfficialWebsite('');
            setTechContactName('');
            setTechContactEmail('');
            setPhone('');
            setBodyText('');
            setIsFormOpen(false);

            setStatusMessage('success');
            fetchPartners();
            setTimeout(() => setStatusMessage(''), 3000);
        } catch (e) {
            console.error('Error saving partner:', e);
            setStatusMessage('error');
        }
    };

    const toggleMarqueeLive = async (partnerId, currentLiveState) => {
        const newLiveState = !currentLiveState;
        try {
            await setDoc(doc(db, 'partners', partnerId), {
                isMarqueeLive: newLiveState,
                updatedAt: new Date().toISOString()
            }, { merge: true });
            fetchPartners();
        } catch (e) {
            console.error('Error toggling marquee state:', e);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this partner profile?')) return;
        try {
            await deleteDoc(doc(db, 'partners', id));
            if (selectedPartner?.id === id) setSelectedPartner(null);
            fetchPartners();
        } catch (e) { console.error(e); }
    };

    const filteredPartners = partners.filter(p => {
      const matchesType = filterType === 'all' || (p.partnershipType || '').toLowerCase() === filterType.toLowerCase();
      const query = searchTerm.toLowerCase();
      const pName = (p.name || '').toLowerCase();
      const pTag = (p.tagline || '').toLowerCase();
      const pContact = (p.techContactName || p.email || '').toLowerCase();
      const matchesSearch = !searchTerm || pName.includes(query) || pTag.includes(query) || pContact.includes(query);
      return matchesType && matchesSearch;
    });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedPartners = [...filteredPartners].sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = (a[sortConfig.key] || '').toString().toLowerCase();
        let bVal = (b[sortConfig.key] || '').toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-[#8B1A2D]/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
                <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="p-2 bg-[#8B1A2D]/20 text-[#8B1A2D] border border-[#8B1A2D]/30 rounded-xl">
                                <Handshake size={18} />
                            </span>
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                                Strategic Alliances &bull; Marquee Control
                            </span>
                        </div>
                        <h1 className="text-3xl font-black text-white uppercase tracking-tight">Institutional Partner Registry</h1>
                        <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-2 max-w-xl">
                            Accredited global universities, corporate foundations, and bilateral technical accords displayed on the live infinite marquee.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsFormOpen(!isFormOpen)}
                            className="bg-[#8B1A2D] hover:bg-[#6c1423] text-white font-black uppercase tracking-widest text-xs px-6 py-3.5 rounded-xl transition-all shadow-lg flex items-center gap-2 cursor-pointer"
                        >
                            <Handshake size={16} /> {isFormOpen ? 'Close Form' : 'Register Strategic Partner'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Comprehensive Registration Form */}
            {isFormOpen && (
              <div className="bg-zinc-950 border border-zinc-800 p-8 rounded-3xl shadow-2xl space-y-6">
                  <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                      <div>
                          <h2 className="text-base font-black uppercase tracking-widest text-[#8B1A2D] flex items-center gap-2">
                              <Handshake size={18} /> Register Strategic Partner &bull; Accord &amp; Marquee Specs
                          </h2>
                          <p className="text-xs text-zinc-400 mt-1">
                              Record legal charters, annual funding commitments, executive liaisons, and live marquee status.
                          </p>
                      </div>
                      <button 
                          onClick={() => setIsFormOpen(false)}
                          className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
                      >
                          <X size={18} />
                      </button>
                  </div>

                  <form onSubmit={handleSavePartner} className="space-y-6">
                      {/* Section 1: Entity Profile */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-[#8B1A2D] flex items-center gap-2">
                              <Building size={14} /> 1. Organization Legal Profile &amp; Location
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Partner Organization Legal Name *</label>
                                  <input 
                                      type="text" 
                                      value={name} 
                                      onChange={e => setName(e.target.value)} 
                                      placeholder="e.g. SUNY Potsdam, USA / NEF Foundation" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Partnership Category</label>
                                  <select 
                                      value={partnershipType} 
                                      onChange={e => setPartnershipType(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D] font-bold"
                                  >
                                      <option>Academic Accreditation Partner</option>
                                      <option>University Campus Partner</option>
                                      <option>Sponsorship & Bursary Authority</option>
                                      <option>Industry Development Partner</option>
                                      <option>International Academic & Media Partner</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Registration / Charter No</label>
                                  <input 
                                      type="text" 
                                      value={registrationNumber} 
                                      onChange={e => setRegistrationNumber(e.target.value)} 
                                      placeholder="e.g. CUE/REG/2012" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">KRA PIN Number</label>
                                  <input 
                                      type="text" 
                                      value={kraPinNumber} 
                                      onChange={e => setKraPinNumber(e.target.value)} 
                                      placeholder="e.g. P051188339K" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Official Platform / Website URL</label>
                                  <input 
                                      type="url" 
                                      value={officialWebsite} 
                                      onChange={e => setOfficialWebsite(e.target.value)} 
                                      placeholder="https://www.potsdam.edu" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">County / Location</label>
                                  <input 
                                      type="text" 
                                      value={county} 
                                      onChange={e => setCounty(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 2: Narrative & Scope */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-[#8B1A2D] flex items-center gap-2">
                              <FileText size={14} /> 2. Partnership Mandate &amp; Accord Terms
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Executive Tagline</label>
                                  <input 
                                      type="text" 
                                      value={tagline} 
                                      onChange={e => setTagline(e.target.value)} 
                                      placeholder="e.g. State University of New York — Exclusive East African Academic Partner" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Legal Accord &amp; Description</label>
                                  <textarea 
                                      rows={3} 
                                      value={bodyText} 
                                      onChange={e => setBodyText(e.target.value)} 
                                      placeholder="Enter complete partnership terms and credit articulation scope..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D] font-mono leading-relaxed"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Meeting Notes &amp; Joint Milestones</label>
                                  <textarea 
                                      rows={3} 
                                      value={meetingNotes} 
                                      onChange={e => setMeetingNotes(e.target.value)} 
                                      placeholder="Log recent executive committee meetings and deliverables..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-white text-xs focus:outline-none focus:border-amber-500 font-mono leading-relaxed"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Annual Funding Commitment (KES)</label>
                                  <input 
                                      type="number" 
                                      value={fundingCommitment} 
                                      onChange={e => setFundingCommitment(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white font-mono text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Active Placements / Slots</label>
                                  <input 
                                      type="number" 
                                      value={activePlacements} 
                                      onChange={e => setActivePlacements(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white font-mono text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 3: Executive POC & Marquee */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-[#8B1A2D] flex items-center gap-2">
                              <User size={14} /> 3. Executive Liaisons &amp; Public Infinite Marquee
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Liaison Executive Name</label>
                                  <input 
                                      type="text" 
                                      value={techContactName} 
                                      onChange={e => setTechContactName(e.target.value)} 
                                      placeholder="e.g. Office of International Education" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Liaison Official Email</label>
                                  <input 
                                      type="email" 
                                      value={techContactEmail} 
                                      onChange={e => setTechContactEmail(e.target.value)} 
                                      placeholder="liaison@partner.org" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Direct Phone</label>
                                  <input 
                                      type="tel" 
                                      value={phone} 
                                      onChange={e => setPhone(e.target.value)} 
                                      placeholder="+254 7XX XXX XXX" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Official Logo Image URL</label>
                                  <input 
                                      type="url" 
                                      value={logoUrl} 
                                      onChange={e => setLogoUrl(e.target.value)} 
                                      placeholder="assets/partner_suny.png" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div className="flex items-center pt-5">
                                  <label className="flex items-center gap-2 cursor-pointer text-xs font-black uppercase tracking-wider text-zinc-300">
                                      <input
                                          type="checkbox"
                                          checked={isMarqueeLive}
                                          onChange={e => setIsMarqueeLive(e.target.checked)}
                                          className="w-4 h-4 accent-[#8B1A2D] rounded"
                                      />
                                      Enable on Infinite Marquee
                                  </label>
                              </div>
                          </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                          <button 
                              type="button" 
                              onClick={() => setIsFormOpen(false)}
                              className="px-6 py-3 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white text-xs font-black uppercase tracking-widest cursor-pointer"
                          >
                              Cancel
                          </button>
                          <button 
                              type="submit" 
                              disabled={statusMessage === 'saving'}
                              className="bg-[#8B1A2D] hover:bg-[#6c1423] text-white font-black uppercase tracking-widest text-xs px-8 py-3 rounded-xl transition-all shadow-lg cursor-pointer"
                          >
                              {statusMessage === 'saving' ? 'Publishing Partner...' : 'Publish Partner to Live Registry'}
                          </button>
                      </div>
                  </form>
                  {statusMessage === 'success' && (
                      <p className="text-emerald-400 text-xs font-bold flex items-center gap-1.5 justify-end">
                          <CheckCircle size={14} /> Partner profile updated and synchronized with live site.
                      </p>
                  )}
              </div>
            )}

            {/* Google Sheets-Grade Modular Registry Sheet with Freeze Panes */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
                {/* Spreadsheet Toolbar */}
                <div className="p-4 border-b border-zinc-800 flex flex-wrap gap-4 justify-between items-center bg-zinc-900/60">
                    <div className="flex flex-1 items-center gap-3 min-w-[280px]">
                      <Search size={16} className="text-zinc-400" />
                      <input 
                        type="text"
                        placeholder="Search by partner name, category, or liaison POC..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full bg-transparent text-sm text-white placeholder-zinc-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Filter size={14} className="text-zinc-400" />
                        <select
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            className="bg-zinc-900 border border-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-widest px-3 py-1.5 rounded-lg focus:outline-none"
                        >
                            <option value="all">All Categories</option>
                            <option value="academic accreditation partner">Accreditation</option>
                            <option value="university campus partner">Campus Partners</option>
                            <option value="sponsorship & bursary authority">Sponsors</option>
                            <option value="industry development partner">Industry</option>
                        </select>
                      </div>

                      <button 
                          onClick={fetchPartners}
                          className="text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full border border-zinc-700 text-zinc-400 bg-zinc-900 hover:text-white transition-all shadow-sm cursor-pointer"
                      >
                          Refresh
                      </button>
                    </div>
                </div>

                {/* Horizontal Scrolling High-Density Table with Freeze Panes */}
                <div className="overflow-x-auto relative">
                  <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                      <thead>
                          <tr className="bg-zinc-900/90 border-b border-zinc-800 select-none">
                              {/* Freeze Pane: Index */}
                              <th className={`w-12 p-3 text-center text-[10px] font-black uppercase tracking-wider text-zinc-500 border-r border-zinc-800 ${
                                pinnedColumn !== 'none' ? 'sticky left-0 z-30 bg-zinc-900' : ''
                              }`}>
                                #
                              </th>

                              {/* Freeze Pane: Partner Name */}
                              <th className={`min-w-[220px] p-0 border-r border-zinc-800 ${
                                pinnedColumn === 'name' 
                                  ? 'sticky left-12 z-30 bg-zinc-900 border-r-2 border-r-[#8B1A2D] shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                  : ''
                              }`}>
                                <div className="w-full h-full p-3 flex items-center justify-between">
                                  <button onClick={() => handleSort('name')} className="flex items-center gap-1 font-black uppercase tracking-widest text-zinc-300 hover:text-white">
                                    <span>Partner Entity</span> {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                                  </button>
                                  <button
                                    onClick={() => setPinnedColumn(pinnedColumn === 'name' ? 'none' : 'name')}
                                    className={`p-1 rounded cursor-pointer ${pinnedColumn === 'name' ? 'text-[#8B1A2D]' : 'text-zinc-600 hover:text-zinc-300'}`}
                                    title={pinnedColumn === 'name' ? 'Unpin column' : 'Pin Partner Name'}
                                  >
                                    <Pin size={11} className={pinnedColumn === 'name' ? 'fill-[#8B1A2D]' : ''} />
                                  </button>
                                </div>
                              </th>

                              {/* Category */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Partnership Category
                              </th>

                              {/* Reg No */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Charter / Reg No
                              </th>

                              {/* Location */}
                              <th className="min-w-[150px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Jurisdiction
                              </th>

                              {/* Platform URL */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Official Website
                              </th>

                              {/* Executive Liaison */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Executive Liaison
                              </th>

                              {/* Contact */}
                              <th className="min-w-[170px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                POC Email &amp; Phone
                              </th>

                              {/* Funding Commitment */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Funding Commitment
                              </th>

                              {/* Placements */}
                              <th className="min-w-[110px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Placements
                              </th>

                              {/* Marquee Display */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 text-center font-black uppercase tracking-widest text-zinc-400">
                                Marquee State
                              </th>

                              {/* Publish State */}
                              <th className="min-w-[110px] p-3 border-r border-zinc-800 text-center font-black uppercase tracking-widest text-zinc-400">
                                Status
                              </th>

                              {/* Actions */}
                              <th className="min-w-[110px] p-3 text-center font-black uppercase tracking-widest text-zinc-400">
                                Action
                              </th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800/60 font-medium">
                          {loading ? (
                              <tr><td colSpan="13" className="p-8 text-center text-zinc-500 text-xs">Loading Partner Registry...</td></tr>
                          ) : sortedPartners.length === 0 ? (
                              <tr><td colSpan="13" className="p-8 text-center text-zinc-500 text-xs">No partners found.</td></tr>
                          ) : sortedPartners.map((partner, idx) => {
                              return (
                                <tr 
                                    key={partner.id} 
                                    onClick={() => setSelectedPartner(partner)}
                                    className="hover:bg-zinc-900/60 transition-colors cursor-pointer group"
                                >
                                    {/* Pinned: Index */}
                                    <td className={`p-3 text-center font-mono text-[11px] text-zinc-500 border-r border-zinc-800 ${
                                      pinnedColumn !== 'none' ? 'sticky left-0 z-20 bg-zinc-950 group-hover:bg-zinc-900' : ''
                                    }`}>
                                        {idx + 1}
                                    </td>

                                    {/* Pinned: Name */}
                                    <td className={`p-3 border-r border-zinc-800 ${
                                      pinnedColumn === 'name' 
                                        ? 'sticky left-12 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r-2 border-r-[#8B1A2D] shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                        : ''
                                    }`}>
                                        <div className="flex items-center gap-2.5">
                                            <img 
                                              src={partner.logoUrl || partner.media?.logoUrl || 'assets/dis_logo.svg'} 
                                              alt="logo" 
                                              className="w-7 h-7 rounded-lg object-contain bg-white p-0.5 border border-zinc-700 shrink-0" 
                                            />
                                            <div>
                                                <p className="font-bold text-white group-hover:text-amber-400 transition-colors">{partner.name}</p>
                                                <p className="text-[10px] text-zinc-500 font-mono truncate max-w-[150px]">{partner.tagline || '-'}</p>
                                            </div>
                                        </div>
                                    </td>

                                    {/* Category */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {partner.partnershipType}
                                    </td>

                                    {/* Reg No */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-zinc-400">
                                        {partner.registrationNumber || '-'}
                                    </td>

                                    {/* Location */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {partner.county || 'Nairobi'}, {partner.country || 'Kenya'}
                                    </td>

                                    {/* Platform URL */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-amber-300 truncate max-w-[180px]">
                                        {partner.platformUrl || partner.integration?.platformUrl || '-'}
                                    </td>

                                    {/* Liaison */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <p className="text-white font-bold">{partner.techContactName || partner.support?.name || '-'}</p>
                                        <p className="text-[10px] text-zinc-500 font-mono">{partner.techContactEmail || partner.email || '-'}</p>
                                    </td>

                                    {/* Contact Phone */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-zinc-400">
                                        {partner.phone || '-'}
                                    </td>

                                    {/* Funding */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-amber-400 font-bold">
                                        KES {(partner.fundingCommitment || 0).toLocaleString()}
                                    </td>

                                    {/* Placements */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-white font-bold">
                                        {partner.activePlacements || 0} Slots
                                    </td>

                                    {/* Marquee State */}
                                    <td className="p-3 border-r border-zinc-800 text-center" onClick={e => e.stopPropagation()}>
                                        <button
                                            onClick={() => toggleMarqueeLive(partner.id, partner.isMarqueeLive !== false)}
                                            className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border transition-all cursor-pointer ${
                                                partner.isMarqueeLive !== false
                                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'
                                                    : 'bg-zinc-800 text-zinc-500 border-zinc-700 hover:text-white'
                                            }`}
                                        >
                                            {partner.isMarqueeLive !== false ? 'Live Marquee' : 'Hidden'}
                                        </button>
                                    </td>

                                    {/* Publish State */}
                                    <td className="p-3 border-r border-zinc-800 text-center">
                                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full border ${
                                          partner.status === 'Published' 
                                            ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' 
                                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                        }`}>
                                          {partner.status || 'Published'}
                                        </span>
                                    </td>

                                    {/* Action */}
                                    <td className="p-3 text-center">
                                        <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                                            <button 
                                                onClick={() => setSelectedPartner(partner)}
                                                className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                                title="Inspect Partner Accord"
                                            >
                                                <Eye size={12} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(partner.id)}
                                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                                                title="Delete Partner"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                              );
                          })}
                      </tbody>
                  </table>
                </div>
            </div>

            {/* Slide-over Full Dossier Drawer */}
            <PartnerDetailDrawer 
              partner={selectedPartner} 
              onClose={() => setSelectedPartner(null)} 
              onToggleMarquee={toggleMarqueeLive}
            />
        </div>
    );
}
